import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Coins, Play, Pause, RefreshCw, AlertOctagon, CheckCircle2, XCircle,
  ArrowUp, ArrowDown, Minus, Shield,
} from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge } from "../../components/dashboard/Shared";

const fmt = (n, d = 2) => (typeof n === "number" ? n.toFixed(d) : n ?? "—");

function BiasPill({ label, data }) {
  const b = data?.b ?? 0;
  const cls = b === 1 ? "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40" :
              b === -1 ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40" :
              "bg-white/5 text-gray-400 border-white/10";
  const Icon = b === 1 ? ArrowUp : b === -1 ? ArrowDown : Minus;
  return (
    <div className={`px-3 py-2 rounded border text-xs flex items-center gap-2 ${cls}`}>
      <Icon size={12} />
      <span className="font-mono">{label}</span>
      <span className="ml-auto tabular text-[10px]">
        {data?.close != null ? `${data.close} vs EMA ${data.ema}` : (data?.reason || "—")}
      </span>
    </div>
  );
}

export default function GoldMTF() {
  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runningCycle, setRunningCycle] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: tr }] = await Promise.all([
        api.get("/goldmtf/status"),
        api.get("/goldmtf/trades?limit=30"),
      ]);
      setStatus(st); setTrades(tr);
    } catch (e) { toast.error("Failed to load GoldMTF status"); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/goldmtf/settings", patch);
      setStatus({ ...status, settings: { ...(status?.settings || {}), ...data } });
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const runCycle = async () => {
    setRunningCycle(true);
    try {
      const { data } = await api.post("/goldmtf/run-cycle");
      console.log("[goldmtf] cycle:", data);
      toast.success(`Cycle: ${data.skipped || data.decision || data.halted || (data.order ? "ORDER PLACED" : "done")}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunningCycle(false); }
  };

  const s = status?.settings || {};
  const bias = status?.current_bias;
  const halted = status?.halted_reason;
  const demoAccs = status?.demo_accounts || [];

  return (
    <div className="space-y-6">
      <PageHeader title="GoldMTF Engine" sub="H1 breakout engine · W1+D1+H4 unanimous bias · pending stop orders · DEMO only.">
        <button data-testid="goldmtf-run-cycle" onClick={runCycle} disabled={runningCycle}
                className="btn-outline text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
          <RefreshCw size={12} className={runningCycle ? "animate-spin" : ""} /> Run cycle
        </button>
      </PageHeader>

      {halted && (
        <div className="p-4 rounded border border-[#FF3B30]/40 bg-[#FF3B30]/10 flex items-center gap-3" data-testid="goldmtf-halt-banner">
          <AlertOctagon size={20} className="text-[#FF3B30]" />
          <div>
            <p className="text-[#FF3B30] font-display">HALTED — settings error</p>
            <p className="text-xs text-gray-300 mt-1">{halted}</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-3">
        <div className="card-dark p-4" data-testid="goldmtf-enabled-card">
          <p className="label-xs">Engine</p>
          <p className={`text-lg font-display ${s.enabled ? "text-[#00F0FF]" : "text-gray-400"}`}>{s.enabled ? "ON" : "OFF"}</p>
          <button data-testid="goldmtf-toggle" onClick={() => save({ enabled: !s.enabled })} disabled={busy || !!halted}
                  className={`mt-2 text-xs px-3 py-1 rounded flex items-center gap-1 ${s.enabled ? "bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40" : "btn-primary"}`}>
            {s.enabled ? <><Pause size={12} /> Stop</> : <><Play size={12} /> Start</>}
          </button>
        </div>
        <div className="card-dark p-4">
          <p className="label-xs">Entries today</p>
          <p className="text-lg font-display tabular">{status?.entries_today ?? 0} / {status?.max_entries_per_day ?? 2}</p>
          <p className="text-[10px] text-gray-500 mt-1">Open: {status?.open_positions ?? 0}</p>
        </div>
        <div className="card-dark p-4">
          <p className="label-xs">Pending order</p>
          <p className={`text-sm ${status?.pending ? "text-[#00F0FF]" : "text-gray-500"}`}>
            {status?.pending ? `${status.pending.direction} STOP @ $${status.pending.entry}` : "—"}
          </p>
        </div>
        <div className="card-dark p-4">
          <p className="label-xs flex items-center gap-1"><Shield size={11} /> Settings hash</p>
          <p className="tabular text-xs text-gray-300">{status?.settings_hash || "—"}</p>
          <p className="text-[10px] text-gray-500 mt-1">Scheduler alive: {status?.scheduler_alive ? "yes" : "no"}</p>
        </div>
      </div>

      <div className="card-dark p-5" data-testid="goldmtf-bias">
        <p className="label-xs mb-3">Multi-timeframe bias</p>
        <div className="grid md:grid-cols-3 gap-3">
          <BiasPill label={`W1 · EMA${s.ema_w1 || 10}`} data={bias?.w1} />
          <BiasPill label={`D1 · EMA${s.ema_d1 || 20}`} data={bias?.d1} />
          <BiasPill label={`H4 · EMA${s.ema_h4 || 50}`} data={bias?.h4} />
        </div>
        <p className="text-xs mt-3">
          Combined bias:
          <span className={`ml-2 font-display ${bias?.direction === "BUY" ? "text-[#00F0FF]" : bias?.direction === "SELL" ? "text-[#FF3B30]" : "text-gray-400"}`}>
            {bias?.direction || "—"}
          </span>
        </p>
      </div>

      <div className="card-dark p-5 space-y-4">
        <p className="label-xs">Settings</p>
        <div className="grid md:grid-cols-3 gap-3">
          <label className="text-xs">
            <span className="label-xs block mb-1">DEMO Account</span>
            <select data-testid="goldmtf-account" value={s.account_id || ""} disabled={busy}
                    onChange={(e) => save({ account_id: e.target.value })} className="input-dark">
              <option value="">— select DEMO account —</option>
              {demoAccs.map((a) => (
                <option key={a.account_id} value={a.account_id}>{a.broker} · {a.login} ({a.name || "demo"})</option>
              ))}
            </select>
            {demoAccs.length === 0 && <p className="text-[10px] text-[#FF3B30] mt-1">No DEMO account connected — engine cannot start.</p>}
          </label>
          <label className="text-xs">
            <span className="label-xs block mb-1">Max concurrent positions</span>
            <input data-testid="goldmtf-max-concurrent" type="number" min="1" max="9999" step="1"
                    defaultValue={s.max_concurrent_positions ?? 999}
                    onBlur={(e) => save({ max_concurrent_positions: parseInt(e.target.value, 10) })} className="input-dark" />
          </label>
          <label className="text-xs">
            <span className="label-xs block mb-1">Risk % per position {s.risk_pct > 1 && <span className="text-amber-400 ml-2">⚠ high</span>}</span>
            <input data-testid="goldmtf-risk-pct" type="number" min="0.1" max="5" step="0.05"
                    defaultValue={s.risk_pct ?? 0.75}
                    onBlur={(e) => save({ risk_pct: parseFloat(e.target.value) })} className="input-dark" />
          </label>
        </div>
      </div>

      <div className="card-dark p-5">
        <p className="label-xs mb-3">Trade History</p>
        {trades.length === 0 ? (
          <p className="text-xs text-gray-500 py-4 text-center">No trades yet — will populate after first order.</p>
        ) : (
          <Table headers={["Time", "Dir", "Entry", "SL", "TP", "Lots", "Status", "PnL", "Exit"]}>
            {trades.slice(0, 25).map((t) => (
              <tr key={t.trade_id} className="border-b border-white/5">
                <Td className="text-[11px] text-gray-500">{new Date(t.created_at).toLocaleString()}</Td>
                <Td className={t.direction === "BUY" ? "text-[#00F0FF]" : "text-[#FF3B30]"}>{t.direction}</Td>
                <Td className="tabular">${fmt(t.entry)}</Td>
                <Td className="tabular text-[#FF3B30]">${fmt(t.sl)}</Td>
                <Td className="tabular text-[#00F0FF]">${fmt(t.tp)}</Td>
                <Td className="tabular">{fmt(t.lots, 2)}</Td>
                <Td><StatusBadge status={t.status === "closed" ? (t.pnl_usd > 0 ? "paid" : "overdue") : t.status === "pending" || t.status === "triggered" ? "pending" : "overdue"} /> <span className="text-[10px] text-gray-500 ml-1">{t.status}</span></Td>
                <Td className={`tabular text-[11px] ${(t.pnl_usd || 0) > 0 ? "text-[#00F0FF]" : (t.pnl_usd || 0) < 0 ? "text-[#FF3B30]" : ""}`}>{t.pnl_usd != null ? `$${fmt(t.pnl_usd)}` : "—"}</Td>
                <Td className="text-[11px] text-gray-500">{t.exit_reason || "—"}</Td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}
